import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { ChevronDown, Crown, Gift, RefreshCw, ScanSearch, ShieldAlert, ShieldCheck, ShieldQuestion, Ticket, UserCheck, X } from 'lucide-react';
import MainLayout from '../layout/MainLayout';
import CenterLayout from '../layout/CenterLayout';
import AppContext from '../Context/AppContext';
import Loader from '../components/Loader';
import { Badge, Empty, HScroll, Modal, Toast } from '../components/userProject/UpUi';
import UpUserPanel from '../components/userProject/UpUserPanel';
import { projectApi } from '../components/userProject/api';

const TABS = [['pending', 'En analyse'], ['granted', 'Accordées'], ['refused', 'Refusées'], ['codes', 'Codes partagés']]
const VERDICT = {
    ok: { label: 'Rien à signaler', tone: 'ok', Icon: ShieldCheck },
    check: { label: 'À vérifier', tone: 'warn', Icon: ShieldQuestion },
    risky: { label: 'Risqué', tone: 'ko', Icon: ShieldAlert },
}
const day = (d) => (d ? moment(d).format('DD/MM/YY HH:mm') : '—')

/**
 * Parrainage (PrimeStation) : tout parrain (user+ ou parrainé devenu actif) dont 5 filleuls sont actifs → récompense à décider ici.
 * Rien ne tourne tout seul côté serveur : « Analyser les comptes » (une fois par jour) donne les codes de parrainage
 * mérités, repère les objectifs atteints (notification au membre : « en analyse ») et analyse ces comptes
 * (IP / appareils partagés, inscriptions en rafale, blocages, avertissements). La récompense = des jours de Premium.
 */
const Referral = () => {
    const { setCurrentPage } = useContext(AppContext)
    const pApi = useMemo(() => projectApi('primestation'), [])
    const [tab, setTab] = useState('pending')
    const [data, setData] = useState(null)
    const [codes, setCodes] = useState(null) // onglet « Codes partagés »
    const [error, setError] = useState('')
    const [busy, setBusy] = useState(null)
    const [lastRun, setLastRun] = useState(null)
    const [decide, setDecide] = useState(null) // { user, grant }
    const [fiche, setFiche] = useState(null)
    const [toast, setToast] = useState(null)
    const notify = useCallback((text, isError = false) => setToast({ text, error: isError, at: Date.now() }), [])

    useEffect(() => { setCurrentPage('parrainage') }, [setCurrentPage])
    const load = useCallback(async () => {
        try {
            const [d, k] = await Promise.all([pApi.get('/referral', { status: tab === 'codes' ? 'pending' : tab }), tab === 'codes' ? pApi.get('/referral/codes') : null])
            setData(d); setCodes(k); setError('')
        } catch (e) { setError(e.message) }
    }, [pApi, tab])
    useEffect(() => { setData(null); load() }, [load])

    const runAll = async () => {
        setBusy('run')
        try {
            setLastRun({ ...(await pApi.post('/referral/run', {})), at: Date.now() })
            await load()
        } catch (e) { notify(e.message, true) }
        setBusy(null)
    }
    const analyze = async (id) => {
        setBusy(`a${id}`)
        try { await pApi.post(`/referral/${id}/analyze`, {}); await load(); notify('Analyse à jour') } catch (e) { notify(e.message, true) }
        setBusy(null)
    }

    const c = data?.counts || {}
    const s = data?.stats || {}
    const cards = [
        [Ticket, 'Codes de parrainage', s.codes, `${(s.used || 0).toLocaleString('fr-FR')} utilisés`],
        [UserCheck, 'Membres qui parrainent', s.sponsors, 'ont reçu leurs codes'],
        [ShieldQuestion, 'En analyse', c.pending, `objectif : ${data?.goal || 5} filleuls actifs`],
        [Crown, 'Récompenses données', c.granted, `${(c.refused || 0).toLocaleString('fr-FR')} refusées`],
    ]

    return (
        <MainLayout>
            <CenterLayout>
                <div className='up-page inv-page ref-page'>
                    <header className='up-head'>
                        <div>
                            <h1>Parrainage</h1>
                            <p className='up-muted'>
                                User+ (invité par l’équipe) : 5 codes. Parrainé : 5 codes une fois actif (10 vraies vidéos, vues à 50 % au moins, en temps réel — le site ne le dit pas).
                                Tout parrain avec 5 filleuls actifs passe ici, en analyse : c’est toi qui décides.
                            </p>
                        </div>
                        <div className='ref-actions'>
                            <button className='pay-act-btn' onClick={load} title='Recharger'><RefreshCw size={16} color='#fff' /></button>
                            <button className='btn cs' onClick={runAll} disabled={!!busy} title='Codes mérités, objectifs atteints, analyse des comptes en attente'>
                                <ScanSearch size={15} /> {busy === 'run' ? 'Analyse…' : 'Analyser les comptes'}
                            </button>
                        </div>
                    </header>

                    {lastRun && (
                        <div className='inv-result'>
                            <b>Analyse faite</b>
                            <span>{lastRun.checked.toLocaleString('fr-FR')} comptes sans codes regardés</span>
                            <span><strong>{lastRun.unlocked}</strong> parrainés ont débloqué leurs codes</span>
                            {lastRun.team > 0 && <span>{lastRun.team} user+ ont reçu leurs codes</span>}
                            <span><strong>{lastRun.goals}</strong> nouvel(s) objectif(s) atteint(s)</span>
                            <span>{lastRun.analyzed} compte(s) en analyse vérifiés · {(lastRun.ms / 1000).toFixed(1)} s</span>
                            <button className='pay-act-btn' onClick={() => setLastRun(null)} title='Fermer'>✕</button>
                        </div>
                    )}

                    <div className='inv-cards ref-cards'>
                        {cards.map(([Icon, label, n, sub]) => (
                            <div key={label} className='inv-card'>
                                <span><Icon size={17} /> {label}</span>
                                <b>{(n || 0).toLocaleString('fr-FR')}</b>
                                <small>{sub}</small>
                            </div>
                        ))}
                    </div>

                    <nav className='ref-tabs'>
                        {TABS.map(([k, label]) => (
                            <button key={k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>{label}{(k === 'codes' ? s.tried : c[k]) ? ` · ${k === 'codes' ? s.tried : c[k]}` : ''}</button>
                        ))}
                    </nav>

                    {error && <p className='up-error'>{error}</p>}
                    {!data && !error && <div className='up-loading'><Loader /></div>}
                    {tab === 'codes' && codes && <TriedCodes pApi={pApi} data={codes} onOpen={setFiche} notify={notify} />}
                    {tab !== 'codes' && data && !data.items.length && (
                        <Empty>{tab === 'pending' ? 'Personne en analyse. Lance « Analyser les comptes » une fois par jour.' : 'Rien ici pour l’instant.'}</Empty>
                    )}
                    <div className='ref-list'>
                        {tab !== 'codes' && data?.items.map((u) => {
                            const check = u.rewardCheck
                            const v = VERDICT[check?.verdict]
                            return (
                                <article key={u.id} className='ref-item'>
                                    <div className='ref-item-head'>
                                        <button className='ref-email' onClick={() => setFiche(u.id)} title='Ouvrir la fiche'>{u.email}</button>
                                        {v && <Badge tone={v.tone}><v.Icon size={13} /> {v.label}</Badge>}
                                        <span className='up-muted'>Objectif atteint {moment(u.rewardAt).fromNow()}</span>
                                        {u.rewardDoneAt && <span className='up-muted'>· {u.rewardStatus === 'granted' ? 'accordée' : 'refusée'} le {day(u.rewardDoneAt)}</span>}
                                        {u.rewardNote && <span className='up-muted'>· « {u.rewardNote} »</span>}
                                    </div>
                                    {check ? (
                                        <>
                                            {check.flags.length > 0 ? (
                                                <ul className='ref-flags'>
                                                    {check.flags.map((f) => <li key={f.text} className={f.level}>{f.text}</li>)}
                                                </ul>
                                            ) : <p className='ref-clean'>Aucun signal : IP, appareils, inscriptions et comptes propres.</p>}
                                            <HScroll>
                                                <div className='ref-table'>
                                                    <div className='ref-row head'><span>Filleul</span><span>Inscrit</span><span>Codes</span><span>Vraies vidéos</span><span>Regardé</span><span>IP</span><span>À noter</span></div>
                                                    {check.members.map((m) => (
                                                        <div key={m.id} className='ref-row'>
                                                            <button className='ref-email' onClick={() => setFiche(m.id)}>{m.email}</button>
                                                            <span>{day(m.createdAt)}</span>
                                                            <span>{m.status !== 'ACTIVE' ? <Badge tone='ko'>{m.status === 'BLOCKED' ? 'Bloqué' : 'Suspendu'}</Badge> : m.active ? <Badge tone='ok'>Actif</Badge> : <Badge>Pas encore</Badge>}</span>
                                                            <span>{m.views}</span>
                                                            <span>{m.watchedHours} h</span>
                                                            <span>{m.ips}</span>
                                                            <span className='ref-notes'>{m.notes.length ? m.notes.join(' · ') : '—'}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </HScroll>
                                            <small className='up-muted'>Analysé {moment(check.at).fromNow()}</small>
                                        </>
                                    ) : <p className='up-muted'>Pas encore analysé.</p>}
                                    <div className='ref-item-actions'>
                                        <button className='btn cw' onClick={() => analyze(u.id)} disabled={!!busy}><ScanSearch size={14} /> {busy === `a${u.id}` ? 'Analyse…' : 'Réanalyser'}</button>
                                        {u.rewardStatus === 'pending' && (
                                            <>
                                                <button className='btn cs' onClick={() => setDecide({ user: u, grant: true })}><Gift size={14} /> Donner le Premium</button>
                                                <button className='btn up-danger-soft' onClick={() => setDecide({ user: u, grant: false })}><X size={14} /> Refuser</button>
                                            </>
                                        )}
                                    </div>
                                </article>
                            )
                        })}
                    </div>
                </div>
                {decide && <Decide pApi={pApi} {...decide} onClose={() => setDecide(null)} onDone={(text) => { notify(text); load() }} notify={notify} />}
                {fiche && <UpUserPanel pApi={pApi} userId={fiche} onClose={() => setFiche(null)} onOpen={setFiche} notify={notify} onChanged={load} />}
                <Toast toast={toast} onDone={() => setToast(null)} />
            </CenterLayout>
        </MainLayout>
    );
};

const ORIGIN = { sponsor: 'Code de parrainage', email: 'Invitation par email', team: 'Code de l’équipe' }

/**
 * Codes essayés après usage : un code déjà utilisé (ou expiré) que d'autres tapent encore a circulé (réseaux, groupes…).
 * Qui l'avait : le parrain (code de parrainage), sinon le destinataire de l'invitation par email / celui qui l'a utilisé.
 * « Partagé » : essayé depuis 2 connexions différentes ou 3 fois. Décision (bloquer…) depuis la fiche du compte.
 */
const TriedCodes = ({ pApi, data, onOpen, notify }) => {
    const [open, setOpen] = useState(null) // id du code déplié
    const [tries, setTries] = useState({}) // id → essais
    const toggle = async (id) => {
        if (open === id) return setOpen(null)
        setOpen(id)
        if (!tries[id]) {
            try { const r = await pApi.get(`/referral/codes/${id}/attempts`); setTries((t) => ({ ...t, [id]: r.items })) } catch (e) { notify(e.message, true) }
        }
    }
    const who = (u) => u ? <button className='ref-email' onClick={() => onOpen(u.id)} title='Ouvrir la fiche'>{u.email}</button> : null
    const status = (u) => u && u.status !== 'ACTIVE' ? <Badge tone='ko'>{u.status === 'BLOCKED' ? 'Bloqué' : 'Suspendu'}</Badge> : null
    return (
        <>
            <p className='up-muted ref-codes-intro'>
                Un code déjà utilisé (ou expiré) que d’autres essaient encore à l’inscription a circulé : réseaux, groupes, forums…
                « Partagé » = essayé depuis 2 connexions différentes ou 3 fois. Le membre a été prévenu sur le site que son compte peut être bloqué.
            </p>
            {data.counts.shared > 0 && <p className='ref-codes-alert'><ShieldAlert size={15} /> {data.counts.shared} code(s) partagé(s) sur {data.counts.tried} essayé(s) après usage</p>}
            {!data.items.length && <Empty>Aucun code essayé après usage pour l’instant.</Empty>}
            {data.items.length > 0 && (
                <HScroll>
                    <div className='ref-table'>
                        <div className='ref-row codes head'><span>Code</span><span>Qui l’avait</span><span>Utilisé par</span><span>Essais</span><span>Connexions</span><span>Dernier essai</span><span /></div>
                        {data.items.map((c) => (
                            <React.Fragment key={c.id}>
                                <div className={`ref-row codes${c.shared ? ' shared' : ''}`}>
                                    <span className='ref-code'>
                                        <b>{c.code}</b>
                                        <small>{ORIGIN[c.origin]}</small>
                                    </span>
                                    <span className='ref-who'>
                                        {c.owner ? <>{who(c.owner)} {status(c.owner)}<small>parrain</small></>
                                            : c.sentTo ? <><span>{c.sentTo}</span><small>reçu par email</small></>
                                                : <small>l’équipe</small>}
                                    </span>
                                    <span className='ref-who'>{c.usedBy ? <>{who(c.usedBy)} {status(c.usedBy)}</> : <small>{c.expiresAt && new Date(c.expiresAt) < new Date() ? 'expiré' : '—'}</small>}</span>
                                    <span>{c.shared ? <Badge tone='ko'>{c.tries} · partagé</Badge> : <Badge tone='warn'>{c.tries}</Badge>}</span>
                                    <span>{c.ips} IP · {c.emails} email{c.emails > 1 ? 's' : ''}</span>
                                    <span>{moment(c.lastTryAt).fromNow()}</span>
                                    <button className='pay-act-btn' onClick={() => toggle(c.id)} title='Voir les essais'>
                                        <ChevronDown size={15} color='#fff' style={{ transform: open === c.id ? 'rotate(180deg)' : 'none' }} />
                                    </button>
                                </div>
                                {open === c.id && (
                                    <div className='ref-tries'>
                                        {!tries[c.id] ? <Loader /> : tries[c.id].map((t) => (
                                            <div key={t.id}><span>{t.email}</span><span>{t.ip || '—'}</span><span>{day(t.at)}</span></div>
                                        ))}
                                    </div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </HScroll>
            )}
        </>
    )
}

/** Accorder (N jours de Premium, ajoutés à sa date de fin) ou refuser, avec une note pour l'équipe. Le membre est notifié. */
const Decide = ({ pApi, user, grant, onClose, onDone, notify }) => {
    const [days, setDays] = useState(30)
    const [note, setNote] = useState('')
    const [busy, setBusy] = useState(false)
    const send = async () => {
        setBusy(true)
        try {
            await pApi.post(`/referral/${user.id}/decide`, { grant, ...(grant && { days: Number(days) }), ...(note.trim() && { note: note.trim() }) })
            onDone(grant ? `${days} jours de Premium donnés à ${user.email}` : `Récompense refusée pour ${user.email}`)
            onClose()
        } catch (e) { notify(e.message, true); setBusy(false) }
    }
    return (
        <Modal title={grant ? 'Donner le Premium' : 'Refuser la récompense'} onClose={onClose}>
            <p className='up-text'>
                {grant
                    ? <>Les jours s’ajoutent à la fin de son Premium actuel (ou à partir d’aujourd’hui). <b>{user.email}</b> reçoit une notification.</>
                    : <><b>{user.email}</b> reçoit une notification : « la récompense n’a pas pu être validée ».</>}
            </p>
            {grant && (
                <>
                    <label className='up-label'>Jours de Premium</label>
                    <input className='up-input' type='number' min={1} max={3650} value={days} onChange={(e) => setDays(e.target.value)} />
                </>
            )}
            <label className='up-label'>Note pour l’équipe (facultatif)</label>
            <input className='up-input' value={note} maxLength={255} onChange={(e) => setNote(e.target.value)} placeholder={grant ? 'ex. comptes vérifiés' : 'ex. mêmes IP, faux comptes'} />
            <div className='up-modal-actions'>
                <button className='btn cw' onClick={onClose}>Annuler</button>
                <button className={`btn ${grant ? 'cs' : 'up-danger'}`} disabled={busy || (grant && !(Number(days) > 0))} onClick={send}>
                    {busy ? <span className='loader sm'></span> : grant ? `Donner ${days} jours` : 'Refuser'}
                </button>
            </div>
        </Modal>
    )
}

export default Referral;
